import type {
    CalendarEvent,
    EventHighlight,
    EventLocation,
    EventStatus,
    PaymentStatus,
    ReservationStatus,
} from './calendar.schema';
import { LOCATIONS } from './calendar.schema';

// ---------------------------------------------------------------------------
// Static catalogs
// ---------------------------------------------------------------------------

export const EVENT_TYPES: Record<string, string> = {
    'yu-gi-oh': 'Yu-Gi-Oh',
    'magic-the-gathering': 'Magic The Gathering',
    'one-piece': 'One Piece',
    pokemon: 'Pokemon',
    riftbound: 'Riftbound',
    'duel-masters': 'Duel Masters',
    'flesh-and-blood': 'Flesh and Blood',
    lorcana: 'Lorcana',
    warhammer: 'Warhammer',
    boardgames: 'Boardgames',
};

export const EVENT_TYPE_OPTIONS = Object.entries(EVENT_TYPES).map(([value, label]) => ({
    value,
    label,
}));

// Short codes drive the `loc` URL param so links stay readable.
export const LOCATION_PARAM: Record<string, EventLocation> = {
    ploiesti: LOCATIONS[0],
    brasov: LOCATIONS[1],
};
export function locationToParam(location: EventLocation): string {
    return location === 'KRIT Brasov' ? 'brasov' : 'ploiesti';
}

type BadgeTone = {
    label: string;
    /** Solid color for the status bar on calendar chips. */
    bar: string;
    /** Pill classes for badges. */
    badge: string;
};

export const STATUS_META: Record<EventStatus, BadgeTone> = {
    draft: {
        label: 'Draft',
        bar: 'bg-amber-400',
        badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    },
    published: {
        label: 'Publicat',
        bar: 'bg-blue-500',
        badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
    },
    open: {
        label: 'Inscrieri deschise',
        bar: 'bg-emerald-500',
        badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
    },
    cancelled: {
        label: 'Anulat',
        bar: 'bg-gray-400',
        badge: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    },
    finalized: {
        label: 'Finalizat',
        bar: 'bg-purple-500',
        badge: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
    },
};

export const RESERVATION_STATUS_META: Record<ReservationStatus, { label: string; badge: string }> = {
    waiting: {
        label: 'In asteptare',
        badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
    },
    done: {
        label: 'Finalizata',
        badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
    },
    cancelled: {
        label: 'Anulata',
        badge: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
    },
};

// Extra badge shown next to the status while a 'waiting' reservation is
// queued (holds no slot).
export const WAITLISTED_META = {
    label: 'Lista de asteptare',
    badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
};

export const PAYMENT_STATUS_META: Record<PaymentStatus, { label: string; badge: string }> = {
    unpaid: {
        label: 'Neplatit',
        badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    },
    paid: {
        label: 'Platit',
        badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
    },
    refunded: {
        label: 'Rambursat',
        badge: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
    },
};

// ---------------------------------------------------------------------------
// Highlights — value/label chips. The KOINS program exists ONLY as the preset
// below (plus stored event data); the API, preview image, storefront and
// admin all render highlights generically. Discontinuing a program = deleting
// its preset entry here.
// ---------------------------------------------------------------------------

export const HIGHLIGHT_PRESETS: Array<{ key: string; label: string; icon: string }> = [
    { key: 'koins', label: 'KOINS', icon: 'koin' },
];

export function highlightSlug(label: string): string {
    return (
        label
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '')
            .slice(0, 40) || 'highlight'
    );
}

// Zero/empty values mean "off" — hidden in every render surface.
export function visibleHighlights(event: CalendarEvent): EventHighlight[] {
    return (event.highlights ?? []).filter((h) => {
        if (!h?.label) return false;
        const value = String(h.value ?? '').trim();
        if (!value) return false;
        const numeric = Number(value);
        return !(Number.isFinite(numeric) && numeric === 0);
    });
}

// Mirror of STATUS_TRANSITIONS in api/controllers/calendar-events — keep them
// in lockstep.
export const STATUS_TRANSITIONS: Record<EventStatus, EventStatus[]> = {
    draft: ['published'],
    published: ['open', 'draft', 'cancelled'],
    open: ['published', 'cancelled'],
    cancelled: ['draft'],
    finalized: [],
};

export const TRANSITION_LABELS: Partial<Record<`${EventStatus}>${EventStatus}`, string>> = {
    'draft>published': 'Publica evenimentul',
    'published>open': 'Deschide inscrierile',
    'published>draft': 'Retrage in draft',
    'published>cancelled': 'Anuleaza evenimentul',
    'open>published': 'Inchide inscrierile',
    'open>cancelled': 'Anuleaza evenimentul',
    'cancelled>draft': 'Restaureaza ca draft',
};

// ---------------------------------------------------------------------------
// Image URLs — event images live on the content bucket with size-prefixed
// filenames (`default_<uuid>.jpg`, `thumb_...`, `original_...`).
// ---------------------------------------------------------------------------

const CONTENT_URL = (process.env.REACT_APP_CONTENT_URL ?? 'https://content.krit.ro/').replace(
    /\/$/,
    '',
);
const S3_PATH = 'public-assets/event-images';

export function eventImageUrl(
    image: string | null | undefined,
    variant: 'default' | 'thumb' | 'original' = 'default',
): string | null {
    if (!image) return null;
    if (image.startsWith('http') || image.startsWith('data:')) return image;
    return `${CONTENT_URL}/${S3_PATH}/${variant}_${image}`;
}

export function previewImageUrl(event: Pick<CalendarEvent, 'id' | 'revision'>): string {
    return `${CONTENT_URL}/${S3_PATH}/preview_${event.id}_r${event.revision ?? 1}.jpg`;
}

// Re-fetch a stored image and return it as a base64 dataURL so a duplicated
// event re-uploads its own copy instead of sharing S3 files with the source.
export async function imageToDataUrl(image: string | null | undefined): Promise<string | null> {
    if (!image) return null;
    if (image.startsWith('data:')) return image;
    const url = eventImageUrl(image, 'original');
    if (!url) return null;
    try {
        const response = await fetch(url);
        if (!response.ok) return null;
        const blob = await response.blob();
        return await new Promise<string | null>((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : null);
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(blob);
        });
    } catch {
        return null;
    }
}

// ---------------------------------------------------------------------------
// Day markers — compact non-event chips rendered inside day cells (used by
// the releases feature to pin release/prerelease dates on the calendar).
// The calendar only knows the generic shape; producers build the map.
// ---------------------------------------------------------------------------

export type DayMarker = {
    /** Unique within its day — feeds React keys. */
    key: string;
    /** The release the marker deep-links to (/v2/releases?open=<id>). */
    releaseId: number;
    /** Short glyph rendered before the title (e.g. a rocket or 'PR'). */
    prefix: string;
    title: string;
};

// ---------------------------------------------------------------------------
// Month-grid math — Monday-first, padded to full weeks.
// ---------------------------------------------------------------------------

export type MonthGrid = {
    /** First visible day (the Monday on/before the 1st). */
    gridStart: Date;
    /** Last visible day, end of day. */
    gridEnd: Date;
    /** Visible days chunked into weeks of 7. */
    weeks: Date[][];
};

export function buildMonthGrid(year: number, month: number): MonthGrid {
    const first = new Date(year, month, 1);
    const offset = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const totalCells = Math.ceil((daysInMonth + offset) / 7) * 7;

    const gridStart = new Date(year, month, 1 - offset);
    const days: Date[] = [];
    for (let i = 0; i < totalCells; i++) {
        days.push(new Date(year, month, 1 - offset + i));
    }
    const weeks: Date[][] = [];
    for (let i = 0; i < days.length; i += 7) {
        weeks.push(days.slice(i, i + 7));
    }
    const last = days[days.length - 1];
    const gridEnd = new Date(last.getFullYear(), last.getMonth(), last.getDate(), 23, 59, 59);
    return { gridStart, gridEnd, weeks };
}

// ---------------------------------------------------------------------------
// Week math — Monday-first, mirroring the month grid.
// ---------------------------------------------------------------------------

export function startOfWeek(d: Date): Date {
    const offset = (d.getDay() + 6) % 7;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate() - offset);
}

export function buildWeek(monday: Date): Date[] {
    return Array.from(
        { length: 7 },
        (_, i) => new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i),
    );
}

/** Parse a `YYYY-MM-DD` week anchor; any day snaps to its Monday. */
export function parseWeekParam(value: string | null): Date {
    if (value) {
        const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
        if (match) {
            const d = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
            if (!isNaN(d.getTime())) return startOfWeek(d);
        }
    }
    return startOfWeek(new Date());
}

export function weekLabel(days: Date[]): string {
    const first = days[0];
    const last = days[days.length - 1];
    const firstLabel = first.toLocaleDateString('ro-RO', {
        day: 'numeric',
        month: 'long',
        ...(first.getFullYear() !== last.getFullYear() ? { year: 'numeric' } : {}),
    });
    const lastLabel = last.toLocaleDateString('ro-RO', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
    return `${firstLabel} – ${lastLabel}`;
}

export function dateKey(d: Date): string {
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${m}-${day}`;
}

export function monthParam(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function parseMonthParam(value: string | null): { year: number; month: number } {
    const now = new Date();
    if (value) {
        const match = /^(\d{4})-(\d{2})$/.exec(value);
        if (match) {
            const year = Number(match[1]);
            const month = Number(match[2]) - 1;
            if (month >= 0 && month <= 11) return { year, month };
        }
    }
    return { year: now.getFullYear(), month: now.getMonth() };
}

export function monthLabel(year: number, month: number): string {
    const label = new Date(year, month, 1).toLocaleDateString('ro-RO', {
        month: 'long',
        year: 'numeric',
    });
    return label.charAt(0).toUpperCase() + label.slice(1);
}

export function formatTime(iso: string): string {
    return new Date(iso).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' });
}

export function formatDateLong(iso: string): string {
    const label = new Date(iso).toLocaleDateString('ro-RO', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
    return label.charAt(0).toUpperCase() + label.slice(1);
}

// Slot holders for the X/capacity counts: signed up ('waiting') or completed
// ('done') and NOT queued on the waitlist. Cancelled/no-show never hold a
// slot; waitlisted sign-ups hold none either until they are promoted.
export function activeReservationCount(event: CalendarEvent): number {
    return (event.reservations ?? []).filter(
        (r) => (r.status === 'waiting' || r.status === 'done') && !r.waitlisted,
    ).length;
}

export function canFinalize(event: CalendarEvent): boolean {
    return event.status !== 'finalized' && new Date(event.start).getTime() < Date.now();
}

// Event already started — past this point the registered headcount is final, so
// count displays drop the live `/capacity` + progress framing.
export function isEventPassed(event: Pick<CalendarEvent, 'start'>): boolean {
    return new Date(event.start).getTime() < Date.now();
}
